import type {
  MenuDataItem,
  Settings as ProLayoutSettings,
} from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import {
  history,
  Link,
  setLocale,
  useAntdConfigSetter,
  useIntl,
} from '@umijs/max';
import { AiChat } from '@zhiyue/ai-chat';
import {
  type LayoutSettings as CoreLayoutSettings,
  type CoreSessionConfig,
  type CurrentUser,
  SessionProvider,
  useSession,
  useShell,
  useTheme,
} from '@zhiyue/core';
import BusinessDetailHost from '@/components/BusinessDetailModal';
import PlatformTheme from '@/components/PlatformTheme';
import { getLayoutThemeToken } from '@/components/PlatformTheme/layoutTokens';
import '@zhiyue/core/style.css';
import '@zhiyue/intelligent-writing/style.css';

import {
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  BarChartOutlined,
  CalendarOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  FileTextOutlined,
  HomeOutlined,
  SettingOutlined,
  TeamOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { theme as antdTheme, Tooltip } from 'antd';
import { BizAppConfig } from 'config/application.manifest';
import {
  getBrandAppName,
  getStoredBrandLocale,
  syncBrandDocument,
} from 'config/brand';
import React from 'react';
import {
  getBootstrapTenantList,
  sessionAdapter,
} from '@/adapters/sessionAdapter';
import {
  AvatarDropdown,
  NotificationIcon,
  ParkSwitcher,
  Question,
  ThemeToggle,
} from '@/components';
import { EnterpriseDetailProvider } from '@/components/EnterpriseDetailModal';
import HeaderLanguage from '@/components/Layout/HeaderLanguage';
import { HEADER_HEIGHT } from '@/components/Layout/layoutConstants';
import shellStyles from '@/components/Layout/ShellLayout.module.less';
import SiderResizeHandle, {
  DEFAULT_SIDER_WIDTH,
  getStoredSiderWidth,
} from '@/components/Layout/SiderResizeHandle';
import ParkBootstrapGate from '@/components/ParkBootstrapGate';
import { SpaceDetailProvider } from '@/components/SpaceDetailModal';
import { isPublicPortalPath, ROUTES } from '@/constants/routes';
import {
  buildOperatorMenus,
  getNavigationTrail,
  navigationTarget,
} from '@/navigation/operatorNavigation';
import {
  collectGroupKeys,
  resolveOpenKeys,
  restoreScrollTop,
} from '@/navigation/sideMenuState';
import {
  type AuthorizedResourceAccessMap,
  buildAuthorizedResourceAccessMap,
} from '@/permissions/adapter';
import {
  clearAutoRefresh,
  consumeApplicationAccessReturnTo,
  ensureValidAccessToken,
  getAccessToken,
  hasOAuth2Session,
  redirectToSSO,
  setupAutoRefresh,
} from '@/services/oauth2';
import {
  getLayoutVisibility,
  updateSessionSnapshot,
} from '@/state/sessionSnapshot';
import defaultSettings from '../config/defaultSettings';
import { readCurrentTenantUuid } from './adapters/tenantStorage';
import { errorConfig } from './requestErrorConfig';
import { getMyAuthorizedResources } from './services/bff/permission.api';
import type { Park } from './services/bff/types/park';
import {
  getParkBootstrapState,
  ParkBootstrapStatus,
  shouldShowOperatorSider,
  subscribeParkBootstrap,
} from './services/parkBootstrap';

export const request: RequestConfig = {
  ...errorConfig,
};

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * 侧栏菜单的滚动位置与手动展开记录。
 * 存在模块作用域，避免路由切换触发 layout 重新渲染时被重置；
 * 菜单 DOM 本身也不再按 pathname 换 key，因此滚动容器不会被卸载。
 */
const sideMenuScrollMemory = { top: 0 };
const sideMenuManualOpen = new Set<string>();
const sideMenuManualClose = new Set<string>();

/**
 * 找到真正在滚动的菜单容器。
 * 壳层给 .ant-pro-sider-menu 设了 overflow-y:auto，ProLayout 外层也有一层
 * overflow:auto；两者都要覆盖，取距离菜单最近、且确实可滚动的那个。
 */
const findMenuScrollContainer = (
  element: Element | null,
): HTMLElement | null => {
  let node = element as HTMLElement | null;
  let found: HTMLElement | null = null;
  while (node && node !== document.body) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      found = node;
      if (node.scrollHeight > node.clientHeight) break;
    }
    node = node.parentElement;
  }
  return found;
};
const isPublicAppPath = (pathname: string) =>
  pathname === loginPath ||
  pathname === '/callback' ||
  isPublicPortalPath(pathname);

const getSettingsWithSiderWidth = (locale?: string) => ({
  ...defaultSettings,
  title: getBrandAppName(locale),
  siderWidth: getStoredSiderWidth(),
});

const filterReadyMenus = (items: MenuDataItem[]): MenuDataItem[] =>
  items.reduce<MenuDataItem[]>((menus, item) => {
    if (item.path === ROUTES.park.setup || item.path === ROUTES.park.create)
      return menus;
    menus.push({
      ...item,
      children: item.children ? filterReadyMenus(item.children) : undefined,
    });
    return menus;
  }, []);

const getUserSyncKey = (
  user?:
    | (Partial<CurrentUser> & {
        uuid?: string;
      })
    | null,
) => {
  if (!user) {
    return '';
  }

  return [user.id, user.uuid, user.username, user.tenantUuid]
    .filter(Boolean)
    .join('|');
};

const getSessionConfig = (): CoreSessionConfig => {
  return {
    redirectAfterLogout: '/user/login',
    adapter: sessionAdapter,
    ui: {
      links: {
        help: 'https://docs.shanhuyun.cn/',
      },
      userMenu: {
        items: [],
      },
      locales: [
        { key: 'en-US', label: 'English', icon: '🇺🇸', title: 'Language' },
        { key: 'ja-JP', label: '日本語', icon: '🇯🇵', title: '言語' },
        { key: 'zh-CN', label: '简体中文', icon: '🇨🇳', title: '语言' },
        { key: 'zh-TW', label: '繁體中文', icon: '🇭🇰', title: '語言' },
      ],
    },
    featureFlags: {
      showHeader: true,
      showNav: true,
      showFooter: true,
      enableTenantSwitch: true,
      enableRegister: false,
    },
    onTenantSwitched: () => {
      const applicationReturnTo = consumeApplicationAccessReturnTo();
      if (applicationReturnTo) {
        window.location.href = applicationReturnTo;
        return;
      }

      window.location.href = '/';
    },
    onLocaleChanged: (locale) => {
      setLocale(locale, false);
    },
    onUnauthorized: () => {
      if (!isPublicAppPath(history.location.pathname)) {
        redirectToSSO();
      }
    },
  };
};

const HeaderTitle: React.FC<{
  logo: React.ReactNode;
  appTitle: string;
  park?: Park;
}> = ({ logo, appTitle, park }) => {
  const [failedCoverUrl, setFailedCoverUrl] = React.useState<string>();
  const coverUrl = park?.cover?.url;
  const title = park?.name || appTitle;

  return (
    <div className="app-header-title-root">
      <button
        type="button"
        className="app-header-title-main"
        onClick={() => history.push(ROUTES.overview)}
      >
        {coverUrl && coverUrl !== failedCoverUrl ? (
          <img
            className="app-header-park-logo"
            src={coverUrl}
            alt=""
            onError={() => setFailedCoverUrl(coverUrl)}
          />
        ) : (
          logo
        )}
        <span className="app-header-title-name" title={title}>
          {title}
        </span>
      </button>
    </div>
  );
};

const SessionSync: React.FC<{ setInitialState: (updater: any) => void }> = ({
  setInitialState,
}) => {
  const { navTheme } = useTheme();
  const { currentUser, loading } = useSession();
  const { shellConfig } = useShell();
  const setAntdConfig = useAntdConfigSetter();
  const [runtimeLocale, setRuntimeLocale] = React.useState(
    getStoredBrandLocale(),
  );
  const themeSyncedRef = React.useRef<CoreLayoutSettings['navTheme'] | null>(
    null,
  );
  const syncedRef = React.useRef({
    navTheme: '',
    userSyncKey: '',
  });
  const userSyncKey = React.useMemo(
    () => getUserSyncKey(currentUser),
    [currentUser],
  );

  React.useEffect(() => {
    updateSessionSnapshot({
      currentUser,
      loading,
      featureFlags: shellConfig.featureFlags,
    });

    if (typeof setInitialState !== 'function') {
      return;
    }

    const lastSynced = syncedRef.current;
    if (
      lastSynced.navTheme === navTheme &&
      lastSynced.userSyncKey === userSyncKey
    ) {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;
      const prevUserSyncKey = getUserSyncKey(prev.currentUser);
      if (
        prev.settings?.navTheme === navTheme &&
        prevUserSyncKey === userSyncKey
      ) {
        return prev;
      }

      return {
        ...prev,
        currentUser,
        settings: {
          ...prev.settings,
          navTheme,
        },
      };
    });

    syncedRef.current = {
      navTheme,
      userSyncKey,
    };
  }, [
    currentUser,
    loading,
    navTheme,
    setInitialState,
    shellConfig.featureFlags,
    userSyncKey,
  ]);

  React.useEffect(() => {
    if (loading) {
      return;
    }

    if (currentUser && hasOAuth2Session()) {
      setupAutoRefresh();
      return;
    }

    clearAutoRefresh();
  }, [currentUser, loading]);

  React.useEffect(() => {
    if (themeSyncedRef.current === navTheme) {
      return;
    }
    themeSyncedRef.current = navTheme;

    const useDarkMode = navTheme === 'realDark';

    setAntdConfig((prevConfig) => {
      const prevTheme = prevConfig?.theme || {};
      const normalized = (
        Array.isArray(prevTheme.algorithm)
          ? prevTheme.algorithm
          : prevTheme.algorithm
            ? [prevTheme.algorithm]
            : []
      ).filter((item) => item !== antdTheme.defaultAlgorithm);
      const hasDarkAlgorithm = normalized.includes(antdTheme.darkAlgorithm);

      if (useDarkMode === hasDarkAlgorithm) {
        return prevConfig;
      }

      const nextAlgorithms = useDarkMode
        ? [
            ...normalized.filter((item) => item !== antdTheme.darkAlgorithm),
            antdTheme.darkAlgorithm,
          ]
        : normalized.filter((item) => item !== antdTheme.darkAlgorithm);

      const nextAlgorithm =
        nextAlgorithms.length === 1
          ? nextAlgorithms[0]
          : nextAlgorithms.length > 1
            ? nextAlgorithms
            : antdTheme.defaultAlgorithm;

      return {
        ...prevConfig,
        theme: {
          ...prevTheme,
          algorithm: nextAlgorithm,
        },
      };
    });
  }, [navTheme, setAntdConfig]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncLocale = () => {
      setRuntimeLocale(getStoredBrandLocale());
    };

    syncLocale();
    window.addEventListener('languagechange', syncLocale);
    window.addEventListener('storage', syncLocale);

    return () => {
      window.removeEventListener('languagechange', syncLocale);
      window.removeEventListener('storage', syncLocale);
    };
  }, []);

  React.useEffect(() => {
    const nextTitle = syncBrandDocument(runtimeLocale);

    if (typeof setInitialState !== 'function') {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;

      const prevSettings = prev.settings || {};
      if (prevSettings.title === nextTitle) {
        return prev;
      }

      return {
        ...prev,
        settings: {
          ...prevSettings,
          title: nextTitle,
        },
      };
    });
  }, [runtimeLocale, setInitialState]);

  return null;
};

export const rootContainer = (
  container: React.ReactNode,
  {
    setInitialState,
  }: { initialState: any; setInitialState: (updater: any) => void },
) => {
  return (
    <RootSessionContainer setInitialState={setInitialState}>
      {container}
    </RootSessionContainer>
  );
};

const RootSessionContainer: React.FC<{
  setInitialState: (updater: any) => void;
  children: React.ReactNode;
}> = ({ setInitialState, children }) => {
  const sessionConfig = React.useMemo(() => {
    if (
      typeof window !== 'undefined' &&
      localStorage.getItem('app_nav_theme') === null
    ) {
      localStorage.setItem('app_nav_theme', 'realDark');
    }
    return getSessionConfig();
  }, []);

  return (
    <SessionProvider config={sessionConfig}>
      <PlatformTheme staticMessages>
        <SessionSync setInitialState={setInitialState} />
        <SpaceDetailProvider>
          <EnterpriseDetailProvider>{children}</EnterpriseDetailProvider>
        </SpaceDetailProvider>
        <FloatingAiChat />
      </PlatformTheme>
    </SessionProvider>
  );
};

const FloatingAiChat: React.FC = () => {
  const { currentUser, loading } = useSession();
  const { navTheme } = useTheme();

  if (loading || !currentUser || isPublicAppPath(history.location.pathname)) {
    return null;
  }

  return (
    <AiChat
      title="AI 助手"
      position="bottom-right"
      theme={navTheme === 'realDark' ? 'dark' : 'light'}
      authHeaders={() => {
        const headers: Record<string, string> = {};
        const token = getAccessToken();
        const tenantUuid = readCurrentTenantUuid() || currentUser.tenantUuid;
        const organizationUuid =
          (
            currentUser as CurrentUser & {
              organizationUuid?: string;
              lastLoginOrganizationUuid?: string;
            }
          ).organizationUuid ||
          (
            currentUser as CurrentUser & {
              lastLoginOrganizationUuid?: string;
            }
          ).lastLoginOrganizationUuid;

        if (token) headers.Authorization = `Bearer ${token}`;
        if (tenantUuid) headers['x-tenant-id'] = tenantUuid;
        if (organizationUuid) headers['x-organization-id'] = organizationUuid;
        return headers;
      }}
    />
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<ProLayoutSettings & CoreLayoutSettings>;
  currentUser?: API.CurrentUser & CurrentUser;
  authorizedResourceAccess?: AuthorizedResourceAccessMap;
  currentTenantIsAdmin?: boolean;
  loading?: boolean;
}> {
  let authorizedResourceAccess: AuthorizedResourceAccessMap = {};
  let currentTenantIsAdmin = false;

  // callback 页面里会在 token 落库后主动 refresh 初始态；
  // 这里不能再排除 /callback，否则首登回跳后权限态会一直是空的。
  if (hasOAuth2Session()) {
    const [authorizedResourcesResult, tenantListResult] =
      await Promise.allSettled([
        getMyAuthorizedResources(BizAppConfig.appSsoClientId || ''),
        getBootstrapTenantList(),
      ]);
    if (authorizedResourcesResult.status === 'fulfilled') {
      authorizedResourceAccess = buildAuthorizedResourceAccessMap(
        authorizedResourcesResult.value,
      );
    }

    if (tenantListResult.status === 'fulfilled') {
      const tenantList = tenantListResult.value ?? [];
      const currentTenantUuid = readCurrentTenantUuid() || tenantList[0]?.id;
      currentTenantIsAdmin = tenantList.some(
        (tenant) =>
          tenant.id === currentTenantUuid && tenant.roleLabel === '管理员',
      );
    }
  }
  return {
    loading: true,
    authorizedResourceAccess,
    currentTenantIsAdmin,
    settings: getSettingsWithSiderWidth(getStoredBrandLocale()) as Partial<
      ProLayoutSettings & CoreLayoutSettings
    >,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const { navTheme } = useTheme();
  const intl = useIntl();
  let navigationMenus: MenuDataItem[] = [];
  const selectedNavigationKeys: string[] = [];
  const [, setParkBootstrapVersion] = React.useState(0);
  // 受控的展开分组：避免 ProLayout 在每次跳页时把用户手动展开的分组覆盖掉
  const [openKeys, setOpenKeys] = React.useState<string[]>([]);
  const menuRootRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(
    () =>
      subscribeParkBootstrap(() =>
        setParkBootstrapVersion((version) => version + 1),
      ),
    [],
  );
  const parkBootstrapReady = shouldShowOperatorSider(
    getParkBootstrapState().status,
  );
  const getCurrentLayoutVisibility = () => {
    const visibility = getLayoutVisibility(history.location.pathname);
    return {
      ...visibility,
      showNav: visibility.showNav && parkBootstrapReady,
    };
  };
  const appTitle = initialState?.settings?.title || getBrandAppName();
  const parkState = getParkBootstrapState();
  const currentPark =
    parkState.status === ParkBootstrapStatus.READY
      ? parkState.activeParks.find(
          (park) => park.uuid === parkState.currentParkUuid,
        )
      : undefined;
  const siderWidth =
    (
      initialState?.settings as Partial<CoreLayoutSettings> & {
        siderWidth?: number;
      }
    )?.siderWidth ?? DEFAULT_SIDER_WIDTH;

  // 视口和折叠状态由组件实时测量，避免窄屏进入后变宽仍不能调宽。
  const resizeEnabled = getCurrentLayoutVisibility().showNav;

  const handleMenuOpenChange = React.useCallback(
    // ProLayout 允许传 false 表示关闭受控（全部收起），这里统一收敛成空数组
    (nextValue: string[] | false) => {
      const nextKeys = nextValue === false ? [] : nextValue || [];
      const prev = new Set(openKeys);
      const next = new Set(nextKeys);
      for (const key of next) {
        if (!prev.has(key)) {
          sideMenuManualOpen.add(key);
          sideMenuManualClose.delete(key);
        }
      }
      for (const key of prev) {
        if (!next.has(key)) {
          sideMenuManualClose.add(key);
          sideMenuManualOpen.delete(key);
        }
      }
      setOpenKeys(nextKeys);
    },
    [openKeys],
  );

  // 路由变化后恢复滚动位置：菜单容器不再重建，这里主要是兜底
  // （例如响应式折叠展开、权限变化导致菜单项增减后浏览器自行回滚）。
  const routeKey = history.location.key;
  React.useEffect(() => {
    const container = findMenuScrollContainer(menuRootRef.current);
    restoreScrollTop(container, sideMenuScrollMemory.top);
  }, [routeKey, openKeys]);

  // 离开页面前记录当前滚动位置
  React.useEffect(() => {
    const container = findMenuScrollContainer(menuRootRef.current);
    if (!container) return undefined;
    const onScroll = () => {
      sideMenuScrollMemory.top = container.scrollTop;
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      sideMenuScrollMemory.top = container.scrollTop;
      container.removeEventListener('scroll', onScroll);
    };
  }, [parkBootstrapReady]);

  const handleResizeWidth = React.useCallback(
    (nextWidth: number) => {
      setInitialState((prev) => {
        if (!prev) return prev;
        const prevSettings =
          (prev.settings as Partial<CoreLayoutSettings> & {
            siderWidth?: number;
          }) || {};
        if (prevSettings.siderWidth === nextWidth) {
          return prev;
        }
        return {
          ...prev,
          settings: {
            ...prevSettings,
            siderWidth: nextWidth,
          },
        };
      });
    },
    [setInitialState],
  );

  return {
    ...initialState?.settings,
    menu: defaultSettings.menu,
    navTheme,
    layout: 'mix',
    splitMenus: true,
    selectedKeys: selectedNavigationKeys,
    openKeys,
    onOpenChange: handleMenuOpenChange,
    className: `${shellStyles.shell} ${navTheme === 'realDark' ? 'park-layout-dark' : 'park-layout-light'}`,
    style: {
      '--shell-header-height': `${HEADER_HEIGHT}px`,
    } as React.CSSProperties,
    contentStyle: {
      height: `calc(100dvh - ${HEADER_HEIGHT}px)`,
      overflow: 'auto',
      minWidth: 0,
    },
    token: getLayoutThemeToken(navTheme),
    actionsRender: () => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) {
        return [];
      }

      return [
        <ParkSwitcher key="park-switcher" />,
        <NotificationIcon key="notification" />,
        <Question key="doc" />,
        ...(parkBootstrapReady
          ? [
              <Tooltip
                key="settings"
                title={intl.formatMessage({ id: 'menu.settings' })}
              >
                <button
                  type="button"
                  className="app-header-settings-btn"
                  aria-label={intl.formatMessage({ id: 'menu.settings' })}
                  onClick={() => {
                    const settings = navigationMenus.find(
                      (item) => item.key === 'nav.settings',
                    );
                    const target = settings && navigationTarget(settings);
                    if (target) history.push(target);
                  }}
                >
                  <SettingOutlined />
                </button>
              </Tooltip>,
            ]
          : []),
        <ThemeToggle key="themeToggle" />,
        <HeaderLanguage key="selectLang" />,
      ];
    },
    avatarProps: {
      render: () => {
        const { showHeader } = getCurrentLayoutVisibility();
        if (!showHeader) {
          return null;
        }
        return <AvatarDropdown />;
      },
    },
    waterMarkProps: {
      content: '',
    },
    footerRender: () => {
      const { showFooter } = getCurrentLayoutVisibility();
      if (!showFooter) {
        return false;
      }
      return null;
    },
    headerRender: (_props, dom) => {
      const { showHeader } = getCurrentLayoutVisibility();
      return showHeader ? dom : false;
    },
    menuRender: (_props, dom) => {
      const { showNav } = getCurrentLayoutVisibility();
      if (!showNav) return false;
      // 不再按 pathname 给菜单换 key：那会让整块 DOM 卸载重建，滚动位置与
      // 手动展开状态在每次跳页时一并丢失。展开态改由 openKeys 受控维护。
      return (
        <div ref={menuRootRef} style={{ display: 'contents' }}>
          {dom}
        </div>
      );
    },
    menuDataRender: (menuData) => {
      navigationMenus = parkBootstrapReady
        ? buildOperatorMenus(
            filterReadyMenus(menuData),
            (id) => intl.formatMessage({ id }),
            {
              tenantAdmin: initialState?.currentTenantIsAdmin,
              icons: {
                'nav.home': <DashboardOutlined />,
                'nav.assets': <HomeOutlined />,
                'nav.attraction': <BankOutlined />,
                'nav.enterprises': <TeamOutlined />,
                'nav.asset': <AppstoreOutlined />,
                'nav.contracts': <FileTextOutlined />,
                'nav.property': <ToolOutlined />,
                'nav.innovation': <CustomerServiceOutlined />,
                'nav.operations': <CalendarOutlined />,
                'nav.hr': <TeamOutlined />,
                'nav.finance': <BankOutlined />,
                'nav.evaluation': <AuditOutlined />,
                'nav.reports': <BarChartOutlined />,
                'nav.settings': <SettingOutlined />,
              },
            },
          )
        : [];
      const trailKeys = getNavigationTrail(
        history.location.pathname,
        navigationMenus,
      )
        .map((item) => item.key)
        .filter((key): key is string => Boolean(key));
      selectedNavigationKeys.splice(
        0,
        selectedNavigationKeys.length,
        ...trailKeys,
      );
      // 按「当前路由链 + 用户手动展开 + 用户手动收起」重算展开分组，
      // 既不丢用户展开的分组，也不把离开的分组一直留着。
      const groupKeys = collectGroupKeys(navigationMenus);
      const parentOf: Record<string, string> = {};
      const walkParents = (items: MenuDataItem[], parent?: string) => {
        for (const item of items) {
          const key = item.key === undefined ? undefined : String(item.key);
          if (!key) continue;
          if (parent) parentOf[key] = parent;
          if (item.children?.length) walkParents(item.children, key);
        }
      };
      walkParents(navigationMenus);
      const nextOpenKeys = resolveOpenKeys({
        matchedKeys: trailKeys.filter((key) => groupKeys.includes(key)),
        openKeys,
        manualOpen: [...sideMenuManualOpen],
        manualClose: [...sideMenuManualClose],
        defaultOpenAll: Boolean(defaultSettings.menu?.defaultOpenAll),
        allGroupKeys: groupKeys,
        parents: parentOf,
      });
      if (nextOpenKeys.join(',') !== openKeys.join(',')) {
        setOpenKeys(nextOpenKeys);
      }
      return navigationMenus;
    },
    menuItemRender: (item, dom) => {
      if (item.isUrl || (item.children && !item.navigationModule)) return dom;
      const target = navigationTarget(item);
      return target ? (
        target.startsWith('https://') ? (
          <a href={target}>{dom}</a>
        ) : (
          <Link to={target} target={item.target}>
            {dom}
          </Link>
        )
      ) : (
        dom
      );
    },
    subMenuItemRender: (item, dom) => {
      if (!item.navigationModule) return dom;
      const target = navigationTarget(item);
      return target ? (
        target.startsWith('https://') ? (
          <a href={target}>{dom}</a>
        ) : (
          <Link to={target}>{dom}</Link>
        )
      ) : (
        dom
      );
    },
    breadcrumbRender: false,
    headerTitleRender: (logo) => (
      <HeaderTitle logo={logo} appTitle={appTitle} park={currentPark} />
    ),
    onPageChange: () => {
      const { pathname } = history.location;
      if (isPublicAppPath(pathname)) {
        return;
      }

      void ensureValidAccessToken();
    },
    menuHeaderRender: undefined,
    collapsedButtonRender: parkBootstrapReady ? undefined : false,
    siderWidth,
    childrenRender: (children) => {
      const { showNav } = getCurrentLayoutVisibility();
      return (
        <>
          <PlatformTheme>
            <ParkBootstrapGate>
              {children}
              <BusinessDetailHost />
            </ParkBootstrapGate>
          </PlatformTheme>
          <SiderResizeHandle
            enabled={showNav && resizeEnabled}
            siderWidth={siderWidth}
            onResizeWidth={handleResizeWidth}
          />
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings: { ...settings, layout: 'mix', splitMenus: true },
                }));
              }}
            />
          )}
        </>
      );
    },
  };
};
